import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import defaultTheme from '@chakra-ui/theme';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import SnackbarProvider from '@sketch-note/design-system/src/components/snackbar/snackbar.component';
import { Error } from '@sketchnote/shared-components';
import { LicenseManager } from 'ag-grid-enterprise';
import axios from 'axios';
import { frontendHostUrl } from 'config';
import { cannyAppId, sentryDSN, sentryEnvironment } from 'config';
import { detect } from 'detect-browser';
import { GetStaticProps } from 'next';
import { AppProps } from 'next/app';
import Head from 'next/head';
import Router, { useRouter } from 'next/router';
import Script from 'next/script';
import { Provider } from 'next-auth/client';
import { DefaultSeo } from 'next-seo';
import NProgress from 'nprogress';
import * as React from 'react';
import { getCookieConsentValue } from 'react-cookie-consent';
import { IntercomProvider } from 'react-use-intercom';
import { LoaderApp } from 'src/components/loaders';
import { useAuth } from 'src/shared/hooks/use-auth';
import { wrapper } from 'src/store/store';
import { theme } from 'src/styles/theme';
import { PageMetaData } from 'src/utils/meta-data';

import { isSiteUnderMaintanence } from '../api/services/authentication/user-auth';
import { LiveActions } from './live-actions/live-actions';

const { useEffect } = React;

Sentry.init({
  dsn: sentryDSN,
  integrations: [new BrowserTracing(), new Sentry.Replay()],
  enabled: process.env.NODE_ENV === 'production',
  // We recommend adjusting this value in production, or using tracesSampler
  // for finer control
  tracesSampleRate: 1.0,
  environment: sentryEnvironment,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

NProgress.configure({ showSpinner: false });

// AG Grid License
LicenseManager.setLicenseKey(process.env.AG_GRID_LICENSE || '');

// Binding events.
Router.events.on('routeChangeStart', () => NProgress.start());
Router.events.on('routeChangeComplete', () => NProgress.done());
Router.events.on('routeChangeError', () => NProgress.done());

const Empty = ({ children }: { children: JSX.Element }) => {
  return <>{children}</>;
};

const _theme = extendTheme(theme, {
  ...defaultTheme,
  styles: { global: {}, button: {} },
  components: {
    ...defaultTheme.components,
    Menu: { parts: [] },
    MenuButton: {},
    MenuList: {},
    Button: {},
    Popover: {},
  },
});

interface MyAppProps extends AppProps {
  Component: {
    (): JSX.Element;
    layout: ({ children }: { children: JSX.Element }) => JSX.Element;
    MetaData: JSX.Element;
  };
}

function MyApp({ Component, pageProps }: MyAppProps) {
  const { setAccessTokenAsync, accessToken } = useAuth((state) => ({
    setAccessTokenAsync: state.setAccessTokenAsync,
    accessToken: state.accessToken,
  }));

  const userDetails = useAuth((s) => s.userDetails);

  const Layout = Component.layout || Empty;

  const MetaData = Component.MetaData;
  const router = useRouter();
  const canonicalUrl = (
    `https://sketchnote.co` + (router.asPath === '/' ? '' : router.asPath)
  ).split('?')[0];

  // React.useEffect(() => {
  //   if (accessToken) {
  //     fetchUserDetails();
  //     if (socket.socket === null) {
  //       socket.initialize(accessToken);
  //     }
  //   }
  // }, [accessToken]);

  React.useEffect(() => {
    if (userDetails?.authentication) {
      localStorage.setItem('authorised', 'authorised');
    } else localStorage.removeItem('authorised');
  }, [userDetails?.authentication]);

  React.useEffect(() => {
    const lastVisitedURL = frontendHostUrl.slice(0, -1) + router.asPath;
    if (userDetails?.authentication) {
      localStorage.setItem('url', lastVisitedURL);
    } else localStorage.removeItem('url');
  }, [router.asPath]);

  React.useEffect(() => {
    if (router?.query?.invite === '1' && router?.query?.orgId) {
      localStorage.setItem('invitedOrg', router?.query?.orgId as string);
    }
  }, [router?.query?.invite]);

  React.useEffect(() => {
    setAccessTokenAsync();

    // Clicking link stays in the same section
    // Expected result is moving to the top of the page.
    // Below is the temporary fix to make it work.
    // TODO: clicking anchor tags should move to top of page when page navigation without turning of the scroll behaviour.
    if (/Firefox/i.test(navigator.userAgent)) {
      document.documentElement.style.scrollBehavior = 'auto';
    }

    // Check cookie consent on load and grant access if user accepted
    window.onload = () => {
      const consent = getCookieConsentValue('sn-cookie-policy');
      if (consent === 'true') {
        const windowT: any = window;
        windowT?.gtag?.('consent', 'update', {
          ad_storage: 'granted',
          analytics_storage: 'granted',
        });
      }
    };
  }, []);

  const handleError = async (error: Error, componentStack: string) => {
    if (process.env.NODE_ENV === 'production') {
      await axios.post('/api/error-report', {
        email: userDetails?.email,
        userId: userDetails?.userId,
        firstName: userDetails?.name,
        errorPage: window.location.href,
        device: detect(),
        errorMessage: error.message || 'No message available!',
        stackTrace: componentStack,
      });
    }
  };

  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback} onError={handleError}>
      <Provider>
        <IntercomProvider appId={process.env.INTERCOM_APP_ID || ''}>
          <ChakraProvider resetCSS={false} theme={_theme}>
            <LoaderApp />
            <SiteUnderMaintenance />
            <Layout>
              <SnackbarProvider>
                <DefaultSeo
                  {...PageMetaData.default}
                  dangerouslySetAllPagesToNoFollow={
                    process?.env?.NEXT_PUBLIC_ENVIRONMENT === 'live' ? false : true
                  }
                  dangerouslySetAllPagesToNoIndex={
                    process?.env?.NEXT_PUBLIC_ENVIRONMENT === 'live' ? false : true
                  }
                />
                {MetaData}
                <div className="h-full page-container">
                  <Sentry.ErrorBoundary fallback={ErrorFallback} onError={handleError}>
                    <Component {...pageProps} />
                  </Sentry.ErrorBoundary>
                </div>
                <LiveActions />
              </SnackbarProvider>
            </Layout>
          </ChakraProvider>
        </IntercomProvider>
        <Head>
          <script async src="https://www.googletagmanager.com/gtag/js?id=G-Y0R2ZPSK9H" />
          <script async src="https://reflio.com/js/reflio.min.js" data-reflio="1m49jbak198ynry" />
          <script
            dangerouslySetInnerHTML={{
              __html: `window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('consent', 'default', {
            'ad_storage': 'denied',
            analytics_storage: 'denied'
          });
          gtag('js', new Date());
          gtag('config', 'G-Y0R2ZPSK9H');
        `,
            }}
          />

          <link rel="canonical" href={canonicalUrl} />
        </Head>
      </Provider>
    </Sentry.ErrorBoundary>
  );
}

const SiteUnderMaintenance = () => {
  const siteUnderMaintanence = useAuth((s) => s.siteUnderMaintanence);
  useEffect(() => {
    setInterval(() => {
      isSiteUnderMaintanence().then((value) => {
        if (value) {
          useAuth.setState({ siteUnderMaintanence: true });
        } else {
          useAuth.setState({ siteUnderMaintanence: false });
        }
      });
    }, 5 * 60 * 1000);
  }, []);

  return siteUnderMaintanence ? (
    <div className="fixed top-0 w-screen h-screen overflow-auto bg-white z-30000">
      <Error
        title="We’re down for maintenance!"
        subTitle="Sketchnote’s currently undergoing scheduled maintenance work. 
        Our tech genies are at work and we’ll be back shortly!"
        image="https://res.cloudinary.com/samvibes/image/upload/v1649671198/website_under_maintenance_91aabc34c7.png"
      />
    </div>
  ) : (
    <></>
  );
};

const ErrorFallback = () => {
  const router = useRouter();
  return (
    <Error
      onHandle={() => {
        router.back();
        setTimeout(() => {
          location.reload();
        }, 1000);
      }}
    />
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return { props: {} };
};

export default wrapper.withRedux(MyApp);
